import type { DashboardSaveV2Request, Validations, VehicleInsurance } from "@credit-acceptance/step6-bff-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { LabeledValue } from "../../../../../components/ui/labeledValue";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { formatCurrencyForDisplay } from "../../../../../components/ui/uikit/currencyInput";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { unmappedMessages } from "../../../serverViolations";
import { useApplyServerFieldErrors } from "../../../useApplyServerFieldErrors";
import { useRegisterSaveableSection } from "../../../useRegisterSaveableSection";
import { useInsuranceProviders } from "../api/useInsuranceProviders";
import { CurrencyField, DateField, FullCoverageField, LossPayeeField, PhoneField, TextField } from "./Fields";
import { translateInsuranceViolations } from "./Insurance.violations";
import {
  AGENT_NAME_MAX_LENGTH,
  buildInsuranceDefaultValues,
  DEDUCTIBLE_MAX_LENGTH,
  type FullCoverageValue,
  type InsuranceInformationInput,
  toInsuranceSaveRequest,
} from "./InsuranceInformation.schema";
import { InsurerNameField } from "./InsurerNameField";

interface InsuranceInformationProperties {
  readonly visible?: boolean;
  readonly insurance?: VehicleInsurance;
  /** `vehicle.validations`; only its `insurance.*` findings are applied here. */
  readonly validations?: Validations;
  /** From `financial.amountFinanced` on the dashboard response — not part of `VehicleInsurance` itself. */
  readonly amountFinanced?: number;
  /** Tab-level role, status/phase and VLA-lock verdict; insurance is not locked by pre-approval. */
  readonly editable?: boolean;
  readonly programType?: string;
  readonly canCall?: boolean;
}

/**
 * Registered with the save orchestrator as "insurance", contributing `vehicleInsurance`.
 * Insurance is not locked by pre-approval (spec 5.1); `editable` is just the tab-level read-only verdict.
 */
export function InsuranceInformation({
  visible = true,
  insurance,
  validations,
  amountFinanced,
  editable = false,
  programType,
  canCall = false,
}: InsuranceInformationProperties) {
  const { t } = useTranslation();
  const disabled = !visible || !editable;
  const isColon = programType === "COLON";
  const [providersRequested, setProvidersRequested] = useState(false);
  const providers = useInsuranceProviders(providersRequested);
  const phoneReference = useRef<HTMLInputElement>(null);
  const [phoneAnnouncement, setPhoneAnnouncement] = useState("");
  // No client rules; `onBlur` only clears a server finding once the analyst leaves the edited field.
  const form = useForm<InsuranceInformationInput>({
    mode: "onBlur",
    defaultValues: buildInsuranceDefaultValues(insurance),
  });
  const { control } = form;
  const fullCoverage = useController({ control, name: "fullCoverage" });
  const insurerName = useController({ control, name: "insurerName" });
  const insurerPhone = useController({ control, name: "insurerPhone" });
  const agentName = useController({ control, name: "agentName" });
  const expirationDate = useController({ control, name: "expirationDate" });
  const comprehensiveDeductibleAmount = useController({ control, name: "comprehensiveDeductibleAmount" });
  const collisionDeductibleAmount = useController({ control, name: "collisionDeductibleAmount" });
  const isCaLossPayee = useController({ control, name: "isCaLossPayee" });

  const { unmapped, apply: applyServerErrors } = useApplyServerFieldErrors(form);
  const applyInsuranceValidations = useCallback(
    (nextValidations: Validations | undefined) =>
      applyServerErrors(translateInsuranceViolations(nextValidations, { fullCoverageVisible: !isColon })),
    [applyServerErrors, isColon]
  );
  // Same as Contract Vehicle: the GET omits `vehicle.validations`, so an absent payload keeps the last save's findings.
  const validationsKey = JSON.stringify(validations);
  useEffect(() => {
    if (validations) {
      applyInsuranceValidations(validations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on stringified content, same as Signers' useServerFieldErrors
  }, [validationsKey, isColon]);

  const [saveError, setSaveError] = useState<string>();
  useRegisterSaveableSection(
    "insurance",
    {
      validate: async () => true,
      getPayload: (): Partial<DashboardSaveV2Request> =>
        disabled ? {} : { vehicleInsurance: toInsuranceSaveRequest(form.getValues()) },
      onSaved: ({ vehicle, refreshFailed }) => {
        setSaveError(undefined);
        // The save response is the source of truth, absent insurance included.
        if (refreshFailed) {
          form.reset(undefined, { keepValues: true, keepDirty: false, keepErrors: true });
        } else {
          form.reset(buildInsuranceDefaultValues(vehicle?.insurance), { keepErrors: true });
        }
        applyInsuranceValidations(vehicle?.validations);
      },
      onSaveFailed: (error) => {
        setSaveError(getErrorMessage(error));
      },
    },
    visible
  );

  const deductiblesDisabled = disabled || fullCoverage.field.value === "N";

  // Selecting "No" disables and immediately nulls out both deductibles.
  // `null`, not `undefined` — see the schema's comment on these two fields for why.
  function handleFullCoverageChange(value: FullCoverageValue) {
    fullCoverage.field.onChange(value);
    if (value === "N") {
      comprehensiveDeductibleAmount.field.onChange(null);
      collisionDeductibleAmount.field.onChange(null);
    }
  }

  // A new company name invalidates the phone that belonged to the previous one.
  function handleInsurerNameChange(value: string) {
    const match = providers.data?.find((provider) => provider.name.toUpperCase() === value.toUpperCase());
    insurerName.field.onChange(match ? value.toUpperCase() : value);
    form.setValue("insurerPhone", match?.phone ?? "", { shouldDirty: true });
    setPhoneAnnouncement(t(match?.phone ? "vehicle.insurance.phoneFilled" : "vehicle.insurance.phoneCleared"));
  }

  function handleInsurerNameBlur() {
    // Normalization is not an edit: it must not overwrite a manually entered phone.
    insurerName.field.onChange(insurerName.field.value.trim().toUpperCase());
    insurerName.field.onBlur();
  }

  if (!visible) {
    return null;
  }

  return (
    <div>
      <SubsectionHeader>{t("vehicle.insurance.heading")}</SubsectionHeader>
      {providers.isError && (
        <p role="alert" className="text-destructive text-2xs px-3 pt-2">
          {t("vehicle.insurance.providersError")}
        </p>
      )}
      <p aria-live="polite" className="sr-only">
        {phoneAnnouncement}
      </p>
      {saveError && (
        <p role="alert" className="text-destructive text-2xs px-3 pt-2">
          {saveError}
        </p>
      )}
      {unmappedMessages(unmapped).map((message, index) => (
        <p key={`${index}-${message}`} role="alert" className="text-destructive text-2xs px-3 pt-2">
          {message}
        </p>
      ))}
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="gap-(--spacing-3) px-(--spacing-3) py-(--spacing-3) flex flex-col">
            <LabeledValue
              label={t("vehicle.insurance.amountFinanced")}
              value={formatCurrencyForDisplay(amountFinanced)}
            />
            <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2">
              {!isColon && (
                <FullCoverageField
                  label={t("vehicle.insurance.fullCoverage")}
                  value={fullCoverage.field.value}
                  onChange={handleFullCoverageChange}
                  error={fullCoverage.fieldState.error}
                  disabled={disabled}
                />
              )}
            </div>
            <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2">
              <InsurerNameField
                label={t("vehicle.insurance.companyName")}
                value={insurerName.field.value}
                onChange={handleInsurerNameChange}
                onBlur={handleInsurerNameBlur}
                onActivate={() => setProvidersRequested(true)}
                error={insurerName.fieldState.error}
                disabled={disabled}
                providers={providers.data ?? []}
                onPhoneFocus={() => phoneReference.current?.focus()}
              />
              <PhoneField
                inputRef={phoneReference}
                canCall={canCall}
                label={t("vehicle.insurance.companyPhone")}
                value={insurerPhone.field.value}
                onChange={insurerPhone.field.onChange}
                onBlur={insurerPhone.field.onBlur}
                error={insurerPhone.fieldState.error}
                disabled={disabled}
              />
              <TextField
                label={t("vehicle.insurance.agent")}
                value={agentName.field.value}
                onChange={agentName.field.onChange}
                onBlur={agentName.field.onBlur}
                error={agentName.fieldState.error}
                disabled={disabled}
                maxLength={AGENT_NAME_MAX_LENGTH}
                uppercase
              />
              <DateField
                preserveInvalidText
                label={t("vehicle.insurance.expirationDate")}
                value={expirationDate.field.value}
                onChange={expirationDate.field.onChange}
                onBlur={expirationDate.field.onBlur}
                error={expirationDate.fieldState.error}
                disabled={disabled}
              />
              <CurrencyField
                label={t("vehicle.insurance.comprehensiveDeductible")}
                value={comprehensiveDeductibleAmount.field.value}
                onChange={(value) => comprehensiveDeductibleAmount.field.onChange(value ?? null)}
                onBlur={comprehensiveDeductibleAmount.field.onBlur}
                error={comprehensiveDeductibleAmount.fieldState.error}
                disabled={deductiblesDisabled}
                maxLength={DEDUCTIBLE_MAX_LENGTH}
              />
              <CurrencyField
                label={t("vehicle.insurance.collisionDeductible")}
                value={collisionDeductibleAmount.field.value}
                onChange={(value) => collisionDeductibleAmount.field.onChange(value ?? null)}
                onBlur={collisionDeductibleAmount.field.onBlur}
                error={collisionDeductibleAmount.fieldState.error}
                disabled={deductiblesDisabled}
                maxLength={DEDUCTIBLE_MAX_LENGTH}
              />
              <LossPayeeField
                label={t("vehicle.insurance.caLossPayee")}
                value={isCaLossPayee.field.value}
                onChange={isCaLossPayee.field.onChange}
                error={isCaLossPayee.fieldState.error}
                disabled={disabled}
              />
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
